import { useMemo } from "react";
import { useSelectorApp } from "../lib/redux";

/**
 * Hook that returns unique lists of Meta (Facebook/Instagram) accounts
 * for use in selects, derived from integrations.facebook in Redux.
 *
 * @returns {{
 *   uniqueInstagramBusinessList: Array<{ integrationID, name, instagramID, pageID }>,
 *   uniqueFacebookPagesList: Array<{ integrationID, id, name, imageURL }>
 * }}
 */
export const useMetaAccountsLists = () => {
  const integrations = useSelectorApp((state) => state.user?.integrations);

  const uniqueInstagramBusinessList = useMemo(() => {
    const metaIntegrations = integrations?.facebook;
    if (!metaIntegrations) return [];

    const integrationList = Object.values(metaIntegrations);
    const accounts = integrationList
      .flatMap((integration) => {
        const pages = integration?.pages || {};
        return Object.values(pages)
          .map((page) => {
            const igBusiness = page?.instagramBusinessAccount;
            if (!igBusiness?.username) return null;
            const adAccountID =
              integration?.businessAccounts &&
              Object.keys(integration.businessAccounts).length > 0
                ? Object.keys(integration.businessAccounts)[0]
                : undefined;
            return {
              integrationID: integration?.id,
              name: igBusiness.username,
              instagramID: igBusiness.id,
              pageID: page?.id,
              adAccountID,
            };
          })
          .filter(Boolean);
      })
      .filter(Boolean);

    const seen = new Set();
    return accounts.filter((account) => {
      if (seen.has(account.instagramID)) return false;
      seen.add(account.instagramID);
      return true;
    });
  }, [integrations?.facebook]);

  const uniqueFacebookPagesList = useMemo(() => {
    const metaIntegrations = integrations?.facebook;
    if (!metaIntegrations) return [];

    const integrationList = Object.values(metaIntegrations);
    const pages = integrationList
      .flatMap((integration) => {
        const pagesObj = integration?.pages || {};
        const adAccountID =
          integration?.businessAccounts &&
          Object.keys(integration.businessAccounts).length > 0
            ? Object.keys(integration.businessAccounts)[0]
            : undefined;
        return Object.values(pagesObj)
          .map((page) => ({
            integrationID: integration?.id,
            name: page?.name,
            id: page?.id,
            imageURL: page?.imageURL,
            adAccountID,
          }))
          .filter(Boolean);
      })
      .filter(Boolean);

    const seen = new Set();
    return pages.filter((page) => {
      if (seen.has(page.id)) return false;
      seen.add(page.id);
      return true;
    });
  }, [integrations?.facebook]);

  return {
    uniqueInstagramBusinessList,
    uniqueFacebookPagesList,
  };
};
